import { useState } from 'react';

import { useEnrichmentValidationSummary } from '../../hooks/use-enrichment-validation';
import {
  type RunValidationTarget,
  useCanShowRunValidation,
  validationRootId,
} from '../../hooks/use-run-validation';
import {
  SCORES_ONLY_ENRICHMENT_SOURCES,
  type ValidationConnectorCode,
} from '../../services/enrichment-validation.service';

import { EnrichmentValidationBanner } from './enrichment-validation-banner';
import { InvalidProductsDrawer } from './invalid-products-drawer';

interface RunValidationPanelProps {
  /** The run's root id (`pipelineWorkflowId ?? workflowId`). */
  rootId: string;
  /** The run's connector — the rule set the summary, drawer and actions are scoped to. */
  connectorCode: ValidationConnectorCode;
  /** The batch's trigger source: scores-only sources (INLINE / FILE / BATCH_UPLOAD) cannot run
   *  validation, which gates "Run validation" on a never-validated run. */
  triggerSource: string | null;
  /** Wraps the panel (spacing). */
  className?: string;
  /** Open the strip expanded (see `EnrichmentValidationBanner.defaultExpanded`). */
  defaultExpanded?: boolean;
  /** Overrides "View products": the host shows the products itself (e.g. `InvalidProductsView`
   *  inside its own panel) instead of this panel's drawer, so drawers never stack. */
  onViewProducts?: () => void;
}

/**
 * One run's validation — run health, "Fix automatically", Re-run validation — with its "View
 * products" drawer. Ungated: callers decide eligibility (see `RunValidationStrip`).
 */
export function RunValidationPanel({
  rootId,
  connectorCode,
  triggerSource,
  className,
  defaultExpanded = false,
  onViewProducts,
}: RunValidationPanelProps) {
  const validation = useEnrichmentValidationSummary(rootId, connectorCode, true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  return (
    <div className={className}>
      <EnrichmentValidationBanner
        workflowId={rootId}
        connectorCode={connectorCode}
        canRunValidation={!SCORES_ONLY_ENRICHMENT_SOURCES.has(triggerSource ?? '')}
        summary={validation.summary}
        isError={validation.isError}
        isAwaitingRevalidation={validation.isAwaitingRevalidation}
        onStartRevalidationWatch={validation.startRevalidationWatch}
        defaultExpanded={defaultExpanded}
        onViewProducts={
          onViewProducts ??
          (() => {
            setDrawerOpen(true);
          })
        }
      />
      {!onViewProducts && (
        <InvalidProductsDrawer
          // Keyed on the run so its filters and page reset for a different batch.
          key={rootId}
          open={drawerOpen}
          onOpenChange={setDrawerOpen}
          workflowId={rootId}
          connectorCode={connectorCode}
          summary={validation.summary}
        />
      )}
    </div>
  );
}

interface RunValidationStripProps {
  /** The batch to validate; nothing renders until it is known and eligible. */
  target: RunValidationTarget | null | undefined;
  /** Wraps the strip (spacing) — only when it renders, so an ineligible batch leaves no gap. */
  className?: string;
}

/**
 * The validation strip for a batch header, shown only where it applies (Google Merchant or Meta run,
 * enrichment complete, `data_quality:read`) — one component so batch detail, the batch export
 * page and the delivery wizard show the same panel under the same rules.
 */
export function RunValidationStrip({ target, className }: RunValidationStripProps) {
  const connectorCode = useCanShowRunValidation(target);
  if (!connectorCode || !target) return null;
  return (
    <RunValidationPanel
      rootId={validationRootId(target)}
      connectorCode={connectorCode}
      triggerSource={target.triggerSource}
      {...(className ? { className } : {})}
    />
  );
}
